import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Flex, FloatButton, Spin } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { ShareButton } from './ShareButton';
import { printPage } from './printPage';
import { goBack } from './goBack';
import ReportPage from './ReportPage';
import { ReportUnavailable } from './sections/ReportUnavailable';
import { ImageModal } from '../report/internalReport/ImageModal';
import { ReportFooter } from '../../common/components/ReportFooter/ReportFooter';
import { printConfig } from '../../pages/Report/printConfig';

// the longest a queued Download PDF waits for a part of the report that is still loading
export const PRINT_WAIT_LIMIT_MS = 15000;

const useStyle = css => {
    useEffect(() => {
        const element = document.createElement('style');
        element.textContent = css;
        document.head.appendChild(element);
        return () => element.remove();
    }, [css]);
};

/**
 * The frame every report type shares: Back and Share (copy link, download PDF) toolbar, the report page, an image viewer for
 * inspection photos and the report footer. `report` is a report view model (see adapters/); null while loading.
 * `unavailable` ({ ownRecord }) replaces the report when the one opened is not the user's own order; `banner` is shown
 * between the toolbar and the report (the upgrade offer), never in the PDF. While `downloadPending` (a part of the
 * report still loading, e.g. the Complete report's AI overview), Download PDF waits for it and then prints.
 */
const ReportTemplate = ({
    report,
    loading,
    showBack = true,
    sharedLink,
    printTitle,
    unavailable,
    banner,
    downloadPending = false,
}) => {
    const { t } = useTranslation(['carReport', 'reportPage']);
    const navigate = useNavigate();
    const [image, setImage] = useState(null);
    useStyle(printConfig);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // a download asked for while part of the report is still loading prints once it is there, or after
    // PRINT_WAIT_LIMIT_MS at the latest, so a request that hangs never leaves the button spinning
    const [printQueued, setPrintQueued] = useState(false);
    const download = () => (downloadPending ? setPrintQueued(true) : printPage(printTitle));
    useEffect(() => {
        if (!printQueued) return undefined;
        const print = () => {
            setPrintQueued(false);
            printPage(printTitle);
        };
        if (!downloadPending) {
            print();
            return undefined;
        }
        const timer = setTimeout(print, PRINT_WAIT_LIMIT_MS);
        return () => clearTimeout(timer);
    }, [printQueued, downloadPending, printTitle]);

    const renderBody = () => {
        if (unavailable) {
            return (
                <div className="cvr">
                    <div className="page">
                        <ReportUnavailable ownRecord={unavailable.ownRecord} />
                    </div>
                </div>
            );
        }
        if (report) return <ReportPage report={report} onOpenImage={setImage} />;
        return (
            <Flex justify="center" style={{ padding: 48 }}>
                {t('noData')}
            </Flex>
        );
    };

    if (loading) {
        return (
            <Flex align="center" justify="center" style={{ minHeight: '60vh' }}>
                <Spin size="large" />
            </Flex>
        );
    }

    return (
        <div className="report-content">
            {image && <ImageModal src={image} onClose={() => setImage(null)} />}
            <div className="cvr-frame">
                <div className="cvr-toolbar print-hide">
                    {showBack ? (
                        <button type="button" className="tb-btn" onClick={() => goBack(navigate)}>
                            <LeftOutlined aria-hidden="true" />
                            {t('reportPage:toolbar.back')}
                        </button>
                    ) : (
                        <span />
                    )}
                    {report && !unavailable && (
                        <ShareButton sharedLink={sharedLink} onDownload={download} waiting={printQueued} />
                    )}
                </div>
                {banner && !unavailable && <div className="cvr-banner print-hide">{banner}</div>}
                {renderBody()}
            </div>
            <FloatButton.BackTop className="print-hide" />
            <ReportFooter />
        </div>
    );
};

export default ReportTemplate;
